import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import { MARKETING_CONSENT_EVENT_NAMES, MARKETING_CONSENT_SOURCE_MODAL } from '@/consts/analytics.consts';
import { FF_MARKETING_CONSENT } from '@/consts/featureFlags';
import { ZIndex } from '@/consts/z-indexes.consts';
import { useTour } from '@/components/ProductTours/TourContext';
import { AuthContext } from '@/contexts/AuthContext';
import { useQueryParamsContext } from '@/contexts/QueryParamsContext';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { I18N_KEYS } from '@/language/keys';
import { log } from '@/logger/logger';
import { useMarketingConsentStore } from '@/state/marketingConsent.state';
import { useWorkflowStore } from '@/state/workflow.state';
import { isFigmaEmbedContext } from '@/utils/auth.utils';
import { AppDialog, AppDialogContent, AppDialogSection } from '@/UI/AppDialog/AppDialog';
import { TextLgSb, TextStdRg } from '@/UI/AppTypography/AppTypography';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { Flex, FlexCol } from '@/UI/styles';
import { getConsentSurfaceId, isWorkflowConsentSurface } from './marketingConsentSurface';

const logger = log.getLogger('MarketingConsent');

/**
 * The decision taken when the user arrived at a surface. Held for as long as they stay there, so
 * eligibility turning on mid-session — the flag rolling out to 100% while someone works on a
 * workflow — cannot pop the modal over their canvas. They get it on their next arrival instead.
 */
interface ConsentArrival {
  surfaceId: string;
  show: boolean;
}

export function MarketingConsentModal() {
  const { t } = useTranslation();
  const { currentUser, isSigningIn } = useContext(AuthContext);
  const { track } = useAnalytics();
  const { value: isFlagOn } = useFeatureFlag(FF_MARKETING_CONSENT);
  const { pathname } = useLocation();
  const { activeTour } = useTour();
  const { queryParamNewRecipe } = useQueryParamsContext();
  const flowGraphLoadingState = useWorkflowStore((state) => state.loadingStates.get('flow-graph'));
  const answered = useMarketingConsentStore((state) => state.answered);
  const markAnswered = useMarketingConsentStore((state) => state.markAnswered);
  const [hiddenLocally, setHiddenLocally] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [arrival, setArrival] = useState<ConsentArrival | null>(null);
  const [hasTourRun, setHasTourRun] = useState(false);

  const surfaceId = useMemo(() => getConsentSurfaceId(pathname), [pathname]);

  // The surface this browser session opened on, so the first-run wait below applies only there.
  const initialSurfaceIdRef = useRef(surfaceId);

  useEffect(() => {
    if (activeTour !== null) {
      setHasTourRun(true);
    }
  }, [activeTour]);

  const isAwaitingFirstRunTour =
    queryParamNewRecipe && surfaceId !== null && surfaceId === initialSurfaceIdRef.current && !hasTourRun;
  const isTourPending = activeTour !== null || isAwaitingFirstRunTour;

  // The modal is portalled above the workflow's PageLoader, so nothing else keeps it off a canvas
  // that hasn't rendered yet. `flow-graph` is the last loading key to flip.
  const isSurfaceReady = !isWorkflowConsentSurface(surfaceId) || flowGraphLoadingState === 'loaded';

  const isEligibilityResolved =
    !isSigningIn && currentUser != null && isFlagOn !== undefined && !isTourPending && isSurfaceReady;

  const isEligible = isFlagOn === true && Boolean(currentUser?.isMarketingConsentEligible) && !isFigmaEmbedContext();

  useEffect(() => {
    if (surfaceId === null || !isEligibilityResolved) {
      return;
    }
    if (arrival?.surfaceId === surfaceId) {
      return;
    }
    setArrival({ surfaceId, show: isEligible });
  }, [surfaceId, isEligibilityResolved, isEligible, arrival]);

  const shouldShow = arrival?.surfaceId === surfaceId && arrival.show && !answered && !hiddenLocally;

  const submitConsent = useCallback(
    async (consent: boolean) => {
      setHiddenLocally(true);
      setIsSubmitting(true);
      try {
        await safeAxios.put(API_ROUTES.users.marketingConsent(), { consent });
        track(
          MARKETING_CONSENT_EVENT_NAMES.ANSWERED,
          { consent, source: MARKETING_CONSENT_SOURCE_MODAL },
          TrackTypeEnum.BI,
        );
        markAnswered();
      } catch (error) {
        // `arrival` is untouched by a failed submit, so the modal simply comes back for a retry.
        setHiddenLocally(false);
        const status = isAxiosError(error) ? error.response?.status : undefined;
        logger.warn('Marketing consent submit failed', { consent, status });
      } finally {
        setIsSubmitting(false);
      }
    },
    [track, markAnswered],
  );

  if (!shouldShow) {
    return null;
  }

  return (
    <AppDialog
      open
      fullWidth
      width={360}
      data-testid="marketing-consent-modal"
      sx={{ zIndex: ZIndex.Overlays.CONFIRMATION_DIALOGS }}
      slotProps={{
        paper: {
          sx: {
            background: token.bg,
            '&&': {
              border: 'none',
              borderRadius: '13px',
            },
          },
        },
      }}
    >
      <AppDialogSection>
        <AppDialogContent sx={{ p: '24px' }}>
          <FlexCol sx={{ gap: 1 }}>
            <TextLgSb>{t(I18N_KEYS.WEAVY_MARKETING.MODALS.CONSENT.TITLE)}</TextLgSb>
            <TextStdRg sx={{ color: token.text }}>{t(I18N_KEYS.WEAVY_MARKETING.MODALS.CONSENT.BODY)}</TextStdRg>
          </FlexCol>
          <Flex sx={{ gap: 1, mt: 4, justifyContent: 'flex-end' }}>
            <ButtonContained
              size="small"
              mode="outlined"
              disabled={isSubmitting}
              onClick={() => void submitConsent(false)}
            >
              {t(I18N_KEYS.WEAVY_MARKETING.MODALS.CONSENT.DECLINE)}
            </ButtonContained>
            <ButtonContained
              size="small"
              disabled={isSubmitting}
              loading={isSubmitting}
              onClick={() => void submitConsent(true)}
            >
              {t(I18N_KEYS.WEAVY_MARKETING.MODALS.CONSENT.ACCEPT)}
            </ButtonContained>
          </Flex>
        </AppDialogContent>
      </AppDialogSection>
    </AppDialog>
  );
}
